import type {
  NotifyCandidateData,
  NotifyCandidateMetadata,
  NotifyCandidateMethod,
} from 'clients/CampaignsClient/CampaignsClient.types';
import type { OutreachOriginValue } from '@/shared/lib/outreach-origin';
import type { ContactCandidateData } from '../useContactCandidateMutation.types';

import {
  isCallData,
  isEmailData,
  isIndeedData,
  isLinkedinData,
  isLinkedinInMailData,
  isWerknlData,
  isWhatsappData,
} from './isChannelData';

const originWhenAiMatches = (isAiLeveraged: boolean, outreachOrigin?: OutreachOriginValue) =>
  isAiLeveraged && outreachOrigin != null ? { origin: outreachOrigin } : {};

const sanitizeText = (value: string = '') => {
  const allowedCharacters = /^[a-zA-Z0-9\s&*():;'"!@\?\{\}]*$/;

  if (!allowedCharacters.test(value)) {
    return value.replace(/[^a-zA-Z0-9\s&*():;'"!@\?\{\}]/g, '');
  }

  return value;
};

type ContactChannelData<T extends NotifyCandidateMethod> = {
  thread_id: string | undefined;
  metadata: NotifyCandidateMetadata | undefined;
  data: ContactCandidateData<T>;
  emailExtras: {
    getFollowUpSchedule: () => Date | Date[];
  };
  werknlExtras: {
    signOff: string;
  };

  queued?: boolean;
};

export const getNotifyCandidateData = <T extends NotifyCandidateMethod>(
  sendMethod: T,
  params: ContactChannelData<T>,
) => {
  const { thread_id, metadata, data: channelData, emailExtras, werknlExtras, queued } = params;

  let notifyCandidateData: NotifyCandidateData;
  switch (sendMethod) {
    case 'call':
      if (isCallData(sendMethod, channelData)) {
        notifyCandidateData = {
          provider_thread_id: thread_id,
          ...(channelData.newLabels && { reasons: channelData.newLabels }),
        };
      }
      break;

    case 'whatsapp':
      if (isWhatsappData(sendMethod, channelData)) {
        notifyCandidateData = {
          message: channelData.message,
          provider_thread_id: undefined,
          is_ai_leveraged: channelData.isAiLeveraged,
          ...originWhenAiMatches(channelData.isAiLeveraged, channelData.outreachOrigin),
          ...(channelData.similarity_score !== undefined && { similarity_score: channelData.similarity_score }),
          ...(channelData.was_ai_message_modified !== undefined && {
            was_ai_message_modified: channelData.was_ai_message_modified,
          }),
          ...(channelData.job_improved_id !== undefined && { job_improved_id: channelData.job_improved_id }),
        };
      }
      break;
    case 'linkedin':
      if (isLinkedinData(sendMethod, channelData)) {
        notifyCandidateData = {
          message: channelData.message,
          provider_thread_id: thread_id,
        };
      }
      break;
    case 'linkedin_inmail':
      if (isLinkedinInMailData(sendMethod, channelData)) {
        notifyCandidateData = {
          subject: channelData.subject,
          message: channelData.message,
          provider_thread_id: thread_id,
          is_ai_leveraged: channelData.isAiLeveraged,
          ...originWhenAiMatches(channelData.isAiLeveraged, channelData.outreachOrigin),
          ...(channelData.similarity_score !== undefined && { similarity_score: channelData.similarity_score }),
          ...(channelData.was_ai_message_modified !== undefined && {
            was_ai_message_modified: channelData.was_ai_message_modified,
          }),
          ...(channelData.job_improved_id !== undefined && { job_improved_id: channelData.job_improved_id }),
        };
      }
      break;
    case 'email':
      if (isEmailData(sendMethod, channelData)) {
        if (!channelData.markAsContacted) {
          notifyCandidateData = {
            subject: channelData.subject,
            message: channelData.message,
            sign_off: channelData.signOff,
            provider_thread_id: undefined,
            is_ai_leveraged: channelData.isAiLeveraged,
            ...originWhenAiMatches(channelData.isAiLeveraged, channelData.outreachOrigin),
            ...(channelData.similarity_score !== undefined && { similarity_score: channelData.similarity_score }),
            ...(channelData.was_ai_message_modified !== undefined && {
              was_ai_message_modified: channelData.was_ai_message_modified,
            }),
            ...(channelData.shouldScheduleFollowUp && {
              schedule_follow_up_to: emailExtras.getFollowUpSchedule(),
            }),
            ...(channelData.job_improved_id !== undefined && { job_improved_id: channelData.job_improved_id }),
          };
        }
      }
      break;
    case 'indeed':
      if (isIndeedData(sendMethod, channelData)) {
        notifyCandidateData = {
          subject: channelData.subject,
          message: channelData.message,
          provider_thread_id: thread_id,
          metadata,
          is_ai_leveraged: channelData.isAiLeveraged,
          ...originWhenAiMatches(channelData.isAiLeveraged, channelData.outreachOrigin),
          ...(channelData.similarity_score !== undefined && { similarity_score: channelData.similarity_score }),
          ...(channelData.was_ai_message_modified !== undefined && {
            was_ai_message_modified: channelData.was_ai_message_modified,
          }),
          ...(channelData.job_improved_id !== undefined && { job_improved_id: channelData.job_improved_id }),
        };
      }
      break;
    case 'werknl':
      if (isWerknlData(sendMethod, channelData)) {
        notifyCandidateData = {
          subject: sanitizeText(channelData.subject),
          message: channelData.message,
          sign_off: werknlExtras.signOff,
          email: channelData.email,
          phone_number: channelData.phoneNumber.replace(/\D/g, ''),
          website: channelData.website,
          provider_thread_id: undefined,
        };
      }
      break;
    default:
      throw new Error(`Unsupported send method: ${sendMethod}`);
  }

  return { ...notifyCandidateData!, status: queued ? ('pending' as const) : ('success' as const) };
};
